/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useAppDispatch } from 'helpers/store'
import { ContactsAction } from 'store/contacts/ContactsAction'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { closeSecondaryModal } from 'helpers/actions'
import { toast } from 'utils/toastUtils'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiFormGrid from 'components/Forms/UiFormGrid'
import CompanyAutocomplete from 'components/CompanyAutocomplete'
import { Company } from 'types/entities/Company'
import { CompanyContact } from 'types/entities/CompanyContact'
import { buildEmailDomainAccountSuggestions } from 'utils/contacts/emailDomainAccountSuggestions'
import {
    extractBusinessEmailDomain,
    getEmailDomainSearchToken,
} from 'utils/email/emailDomainUtils'
import { getCompanyDisplayName } from 'utils/companyUtils'

interface AssignContactToCompanyFormProps {
    contactId: number
    contact?: CompanyContact | null
    onSuccess?: () => void
}

const AssignContactToCompanyForm: React.FC<AssignContactToCompanyFormProps> = ({
    contactId,
    contact = null,
    onSuccess,
}) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [suggestedCompanies, setSuggestedCompanies] = useState<Company[]>([])
    const [matchedDomain, setMatchedDomain] = useState<string | null>(null)

    useEffect(() => {
        const email = contact?.email
        const domain = extractBusinessEmailDomain(email)
        if (!domain) {
            setSuggestedCompanies([])
            setMatchedDomain(null)
            return
        }

        let cancelled = false
        const searchToken = getEmailDomainSearchToken(email)
        if (!searchToken) {
            setSuggestedCompanies([])
            setMatchedDomain(domain)
            return
        }

        dispatch(CompaniesAction.fetchGetCompanyContactFromAutocomplete(searchToken))
            .then((matchedContacts) => {
                if (cancelled) {
                    return
                }
                const suggestion = buildEmailDomainAccountSuggestions(email, matchedContacts || [], {
                    limit: 5,
                })
                setMatchedDomain(suggestion?.domain ?? domain)
                setSuggestedCompanies(suggestion?.companies ?? [])
            })
            .catch(() => {
                if (!cancelled) {
                    setSuggestedCompanies([])
                    setMatchedDomain(domain)
                }
            })

        return () => {
            cancelled = true
        }
    }, [contact?.email, dispatch])

    const handleSubmit = async () => {
        if (!selectedCompany) {
            toast.error(t('Please select a company'))
            return
        }

        setIsSubmitting(true)
        try {
            await dispatch(ContactsAction.fetchAssignToCompany(contactId, selectedCompany))
            toast.success(t('Contact assigned to company successfully'))
            if (onSuccess) {
                onSuccess()
            }
            dispatch(closeSecondaryModal())
        } catch {
            toast.error(t('Failed to assign contact to company'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={clsx('AssignContactToCompanyForm flex flex-col grow')}>
            <UiFormInner title={t('Assign to Company')}>
                <UiFormGrid cols={1} gap={6} responsive={false}>
                    {matchedDomain ? (
                        <div className="rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-3 py-2 text-sm">
                            {t(
                                'Linking also checks matching email domain @{{domain}} with other contacts.',
                                { domain: matchedDomain }
                            )}
                        </div>
                    ) : null}

                    {suggestedCompanies.length > 0 ? (
                        <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                {t('Suggested accounts (same email domain)')}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {suggestedCompanies.map((company) => (
                                    <button
                                        key={company.id}
                                        type="button"
                                        className={clsx(
                                            'rounded-md border px-3 py-1.5 text-sm transition-colors',
                                            selectedCompany?.id === company.id
                                                ? 'border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-900/40 dark:text-blue-100'
                                                : 'border-gray-300 bg-white text-gray-800 hover:border-blue-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'
                                        )}
                                        onClick={() => setSelectedCompany(company)}
                                    >
                                        {getCompanyDisplayName(company) || company.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <CompanyAutocomplete
                        key={selectedCompany?.id ?? 'unselected'}
                        name="company_id"
                        label={t('Select Company')}
                        onSelect={(company) => setSelectedCompany(company)}
                        defaultValue={selectedCompany}
                        required
                    />
                </UiFormGrid>
            </UiFormInner>

            <UiFormActionsBar
                primaryId={'btn-assign-contact-to-company-save'}
                primaryText={isSubmitting ? t('Assigning...') : t('Assign')}
                onPrimary={handleSubmit}
                loading={isSubmitting}
            />
        </div>
    )
}

export default AssignContactToCompanyForm
